import React, { useState, useMemo, useEffect } from 'react';
import { Check } from 'lucide-react';
import SearchOverlay from 'components/SearchOverlay';
import Button from 'components/Button';
import { Permission } from 'types/permission';
import styles from './Role.module.scss';

interface PermissionSelectorModalProps {
  show: boolean;
  onHide: () => void;
  permissions: Permission[];
  selectedPermissionIds: string[];
  onConfirm: (ids: string[]) => void;
  loading?: boolean;
}

const PermissionSelectorModal: React.FC<PermissionSelectorModalProps> = ({
  show,
  onHide,
  permissions,
  selectedPermissionIds,
  onConfirm,
  loading,
}) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [tempSelectedIds, setTempSelectedIds] = useState<string[]>([]);

  // Initialize tempSelectedIds when modal opens
  useEffect(() => {
    if (show) {
      setTempSelectedIds(selectedPermissionIds);
      setSearchQuery('');
    }
  }, [show, selectedPermissionIds]);

  const filteredPermissions = useMemo(() => {
    const query = searchQuery.toLowerCase().trim();
    if (!query) return permissions;
    return permissions.filter(
      (p) =>
        p.name.toLowerCase().includes(query) ||
        p.key.toLowerCase().includes(query),
    );
  }, [permissions, searchQuery]);

  const handleToggle = (id: string) => {
    setTempSelectedIds((prev: string[]) => {
      const isSelected = prev.includes(id);
      if (isSelected) {
        return prev.filter((pId: string) => pId !== id);
      } else {
        return [...prev, id];
      }
    });
  };

  const handleConfirm = () => {
    onConfirm(tempSelectedIds);
    onHide();
  };

  return (
    <SearchOverlay
      isOpen={show}
      onClose={onHide}
      query={searchQuery}
      onQueryChange={setSearchQuery}
      placeholder="Search permissions..."
      footer={
        <div className={styles.selectorFooter}>
          <div className={styles.selectedCount}>
            {tempSelectedIds.length} permissions selected
          </div>
          <div className={styles.footerActions}>
            <Button variant="outline" onClick={onHide}>
              Cancel
            </Button>
            <Button onClick={handleConfirm}>Done</Button>
          </div>
        </div>
      }
    >
      <div className={styles.selectorBody}>
        {loading ? (
          <div className={styles.loadingState}>Loading permissions...</div>
        ) : filteredPermissions.length === 0 ? (
          <div className={styles.emptyState}>
            {searchQuery
              ? 'No permissions match your search.'
              : 'No permissions found.'}
          </div>
        ) : (
          <div className={styles.selectorList}>
            {filteredPermissions.map((permission) => {
              const isSelected = tempSelectedIds.includes(permission.id);
              return (
                <div
                  key={permission.id}
                  className={`${styles.selectorItem} ${isSelected ? styles.selected : ''}`}
                  onClick={() => handleToggle(permission.id)}
                >
                  <div className={styles.selectorItemInfo}>
                    <span className={styles.permissionName}>
                      {permission.name}
                    </span>
                    <span className={styles.permissionKey}>
                      {permission.key}
                    </span>
                  </div>
                  {isSelected && (
                    <div className={styles.checkIcon}>
                      <Check size={18} />
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>
    </SearchOverlay>
  );
};

export default PermissionSelectorModal;
